import { Button } from '@remote-com/norma/core/button';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { contentfulFieldHasContent } from 'remote-com-shared/domains/contentful/helpers';
import {
  imageEntryToPrismicFormat,
  isContentfulRichText,
  RenderFieldText,
  universalFieldHasContent,
} from 'remote-com-shared/domains/contentful/prismicBridge';
import { transformProperties } from 'remote-com-shared/domains/prismic/helpers';
import styled, { css } from 'styled-components';

import { pixelToRem } from '../../style/helpers';
import { breakpointFrom } from '../../style/mediaQueries';
import { HubspotFormModalButton } from '../HubspotForm';

import {
  BannerBody,
  BannerContent,
  BannerDescription as BannerDescriptionRaw,
  BannerHead,
  BannerPosition,
  BannerTitle,
  BannerWrapper,
  CTAButton,
  getIdForBanner,
  IllustrationWrapper,
} from './sharedStyles';

const BannerDescription = styled(BannerDescriptionRaw)`
  font-size: 18px;
`;

const CtaContainer = styled.div`
  display: grid;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: center;
  line-height: 1.3;
  grid-template-columns: auto;
  gap: 8px;
  align-items: stretch;
  margin-top: 32px;

  ${breakpointFrom.md(css`
    grid-template-columns: 1fr 1fr;
  `)}
`;

const CTAButtonOutline = styled(Button).attrs({
  variant: 'solid',
  shadow: true,
})`
  && {
    cursor: pointer;
    padding: 6px 24px;
    color: var(--colors-blank);
    text-transform: uppercase;
    font-size: ${pixelToRem('13px')};
    font-weight: 400;
    letter-spacing: 0.1em;
    line-height: 1.3;
  }
`;

export function CallToAction(props) {
  const [bannerHeight, setBannerHeight] = useState(500);

  // slice is defined for light backwards compatibility with panda which uses prismic slices
  const { slice, context } = transformProperties(props);
  let { entry } = props;

  if (slice && !entry) {
    entry = transformProperties(slice.primary);
  }

  const {
    buttonLabel,
    buttonLink,
    utmSource,
    illustration: illustrationSource,
    hubspotFormId,
    modalButtonLabel,
    hubspotModalFormTitle,
    hubspotModalFormDescription,
  } = entry;
  const bannerRef = useRef(null);
  const {
    description: descriptionSource,
    image: prismicImage,
    backgroundImage: backgroundImageSource = prismicImage,
    title,
  } = context.banner || transformProperties(context.primary.banner);
  const { size, anchorId } = context.bannerInstance || transformProperties(context.primary);
  const { reducedSpacing, marketingBannerFirstBlock } = context;

  let description = descriptionSource;
  if (isContentfulRichText(descriptionSource)) {
    description = descriptionSource.json;
  }

  const constructUtmLink = (source) => {
    if (!source) {
      return '';
    }
    return `?utm_source=${source}`;
  };

  const image = imageEntryToPrismicFormat(backgroundImageSource);
  const illustration = imageEntryToPrismicFormat(illustrationSource);

  useEffect(() => {
    setBannerHeight(bannerRef?.current?.clientHeight);
  }, []);

  return (
    <BannerPosition
      $sticky={size}
      $bannerHeight={bannerHeight}
      $marketingBannerFirstBlock={marketingBannerFirstBlock}
    >
      <BannerWrapper
        id={getIdForBanner(anchorId)}
        ref={bannerRef}
        $backgroundImage={image}
        $size={size}
        $reducedSpacing={reducedSpacing}
      >
        {contentfulFieldHasContent(title) && (
          <BannerHead>
            <BannerTitle>{title}</BannerTitle>
          </BannerHead>
        )}
        <BannerBody $illustration={illustration}>
          <BannerContent>
            {universalFieldHasContent(description) && (
              <BannerDescription>
                <RenderFieldText field={description} />
              </BannerDescription>
            )}

            <CtaContainer>
              {buttonLink && contentfulFieldHasContent(buttonLabel) && (
                <Link href={`${buttonLink}/${constructUtmLink(utmSource)}`} passHref legacyBehavior>
                  <CTAButton>{buttonLabel}</CTAButton>
                </Link>
              )}

              {contentfulFieldHasContent(modalButtonLabel) && (
                <HubspotFormModalButton
                  formId={hubspotFormId}
                  formTitle={hubspotModalFormTitle}
                  formSubhead={hubspotModalFormDescription}
                  as={CTAButtonOutline}
                >
                  {modalButtonLabel}
                </HubspotFormModalButton>
              )}
            </CtaContainer>
          </BannerContent>
          {illustration && (
            <IllustrationWrapper>
              <Image
                src={illustration.url}
                alt={illustration.alt || ''}
                height={illustration.dimensions.height}
                width={illustration.dimensions.width}
                objectFit="contain"
                objectPosition="bottom right"
              />
            </IllustrationWrapper>
          )}
        </BannerBody>
      </BannerWrapper>
    </BannerPosition>
  );
}
